import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { descargar, imprimirPdf } from "@/lib/download";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, FileSpreadsheet, FileText, Printer, Eye, EyeOff } from "lucide-react";

const COLS = ["Pedido", "Cliente", "Cant.", "Producto", "Tamaño", "Estilo", "Color", "Notas"];

const Encabezado = ({ id, txt, qty, lvl, isOpen, toggle }) => (
  <tr className={lvl === 0 ? "bg-indigo-100" : "bg-indigo-50"}>
    <td colSpan={8} className="p-0">
      <button type="button" onClick={() => toggle(id, !isOpen)} className={`w-full flex items-center justify-between px-3 py-1.5 text-left ${lvl ? "pl-7" : ""}`} data-testid={`resumen-${lvl ? "estilo" : "cat"}-${txt}`}>
        <span className="flex items-center gap-1 text-sm font-bold">{isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}{lvl ? "Estilo: " : "Productos: "}{txt}</span>
        <span className="font-mono text-sm font-bold">{qty}</span>
      </button>
    </td>
  </tr>
);

const Fila = ({ r }) => (
  <tr className={`align-top border-b border-zinc-100 text-xs ${r[8] ? "bg-red-50" : ""}`} data-testid={`resumen-fila-${r[0]}`}>
    {r.slice(0, 8).map((v, k) => (
      <td key={k} className={`px-2 py-1 ${k === 0 ? "font-mono font-semibold whitespace-nowrap" : ""} ${k === 2 ? "font-bold text-center" : ""} ${k === 7 ? "whitespace-pre-wrap min-w-[160px] text-zinc-600" : ""}`}>{v}</td>
    ))}
  </tr>
);

// Resumen: Categoría → Estilo → una fila por producto de cada pedido (mismos datos que el Excel/PDF).
export const ResumenFabricacion = ({ open, onOpenChange }) => {
  const [data, setData] = useState({ categorias: [], total: 0 });
  const [abiertos, setAbiertos] = useState({});
  const [showPed, setShowPed] = useState(true);
  const load = () => api.get("/fabricacion/resumen-cat").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    if (!open) return undefined;
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(t);
  }, [open]);
  const modo = showPed ? "detallado" : "general";
  const run = (fn) => fn().catch((e) => toast.error(String(e.message || e)));
  const path = (fmt) => `/fabricacion/resumen.${fmt}?modo=${modo}`;
  const toggle = (id, v) => setAbiertos((p) => ({ ...p, [id]: v }));
  const rows = [];
  data.categorias.forEach((c) => {
    const cOpen = abiertos[c.nombre] ?? true;
    rows.push(<Encabezado key={c.nombre} id={c.nombre} txt={c.nombre} qty={c.cantidad} lvl={0} isOpen={cOpen} toggle={toggle} />);
    if (!cOpen) return;
    c.estilos.forEach((e) => {
      const id = `${c.nombre}/${e.nombre}`;
      const eOpen = abiertos[id] ?? true;
      rows.push(<Encabezado key={id} id={id} txt={e.nombre} qty={e.cantidad} lvl={1} isOpen={eOpen} toggle={toggle} />);
      if (eOpen && showPed) e.filas.forEach((r, k) => rows.push(<Fila key={`${id}-${k}`} r={r} />));
    });
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="resumen-fab-dialog">
        <DialogHeader><DialogTitle>Resumen de Fabricación</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowPed((v) => !v)} data-testid="resumen-toggle-pedidos">
            {showPed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {showPed ? "Ocultar pedidos" : "Mostrar pedidos"}
          </Button>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => run(() => imprimirPdf(path("pdf")))} data-testid="resumen-print"><Printer className="w-4 h-4" /> Imprimir</Button>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => run(() => descargar(path("xlsx"), `resumen_fabricacion_${modo}.xlsx`))} data-testid="resumen-xlsx"><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => run(() => descargar(path("pdf"), `resumen_fabricacion_${modo}.pdf`))} data-testid="resumen-pdf"><FileText className="w-4 h-4" /> PDF</Button>
          <span className="text-[11px] text-zinc-500 self-center">Exporta {showPed ? "detallado (con pedidos y notas)" : "general"}</span>
        </div>
        <div className="border border-zinc-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#002FA7] text-white text-[10px] uppercase tracking-widest">
              <tr>{COLS.map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-400 py-8">Sin pedidos activos.</td></tr>}
              {rows}
              <tr className="bg-zinc-900 text-white font-bold" data-testid="resumen-total"><td colSpan={2} className="px-3 py-2">TOTAL GENERAL (unidades pendientes)</td><td className="px-2 py-2 font-mono text-center">{data.total}</td><td colSpan={5} /></tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
